import { FC, useCallback } from 'react';

import {
  Document,
  transcriptorApi,
} from '@/shared/api/transcriptor/transcriptorApi';
import { useAppDispatch } from '@/shared/hooks';

import {
  TranscriptorConsultation,
  transcriptorModel,
} from '@/entities/transcriptor';

import { ConsultationNode, dentalConsultationMessages } from '../../config';
import {
  convertNodesToDocumentUpdate,
  isDocumentFailedWithoutContent,
  isDocumentGenerating,
  parseLiteratureReviewData,
} from '../../lib';
import { ConsultationEmptyState } from '../ConsultationEmptyState/ConsultationEmptyState';
import { ConsultationWidget } from '../ConsultationWidget/ConsultationWidget';
import { LiteratureReferences } from '../LiteratureReferences';

import styles from './LiteratureReviewWidget.module.scss';

type LiteratureReviewWidgetProps = {
  consultation?: TranscriptorConsultation | null;
  literatureReview?: Document | null;
  onRegenerate?: () => void;
};

export const LiteratureReviewWidget: FC<LiteratureReviewWidgetProps> = ({
  consultation,
  literatureReview,
  onRegenerate,
}) => {
  const dispatch = useAppDispatch();

  const consultationId = consultation?.id;
  const patientId = consultation?.patientId;
  const status = consultation?.status;
  const literatureReviewId = literatureReview?.id;
  const documentStatus = literatureReview?.status;

  const handleSave = useCallback(
    async (editedData: ConsultationNode[]) => {
      if (!consultationId) {
        throw new Error('Consultation ID is missing');
      }

      if (!literatureReviewId) {
        throw new Error('Literature review document is not yet generated');
      }

      const updateRequest = convertNodesToDocumentUpdate(editedData);
      await transcriptorApi.updateConsultationDocument(
        consultationId,
        literatureReviewId,
        updateRequest,
      );

      dispatch(
        transcriptorModel.thunks.fetchConsultationById({
          consultationId,
          patientId,
        }),
      );
    },
    [consultationId, literatureReviewId, patientId],
  );

  if (!literatureReview) {
    return (
      <ConsultationEmptyState
        status={status}
        noDataMessage={dentalConsultationMessages.noLiteratureReviewAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  if (
    documentStatus === 'skipped' ||
    isDocumentGenerating(literatureReview) ||
    isDocumentFailedWithoutContent(literatureReview)
  ) {
    return (
      <ConsultationEmptyState
        status={status}
        documentStatus={documentStatus}
        documentError={literatureReview?.error}
        noDataMessage={dentalConsultationMessages.noLiteratureReviewAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  const data = parseLiteratureReviewData(literatureReview);
  const references = literatureReview.references || null;

  return (
    <>
      <ConsultationWidget
        data={data}
        showActions
        widgetType="summary"
        transcriptChunks={consultation?.transcriptChunks}
        literatureReferences={references}
        onSave={handleSave}
      />
      {references && references.length > 0 && (
        <div className={styles.referencesContainer}>
          <LiteratureReferences references={references} />
        </div>
      )}
    </>
  );
};
